/* Reusable sign-in components, drawn with plain DOM and styled only by theme tokens
   (see public/css/rfhq_auth.css). They hold no wording and no rules: the caller passes
   text, lengths and callbacks. onboardinghq pages reuse them as window.rfhqUI. */
(function () {
	const h = (tag, attrs, ...kids) => {
		const el = document.createElement(tag);
		for (const [key, value] of Object.entries(attrs || {})) {
			if (value === false || value == null) continue;
			if (key === "class") el.className = value;
			else if (key === "text") el.textContent = value;
			else if (key.startsWith("on")) el.addEventListener(key.slice(2), value);
			else el.setAttribute(key, value === true ? "" : value);
		}
		kids.flat().forEach((kid) => kid != null && el.append(kid.nodeType ? kid : document.createTextNode(kid)));
		return el;
	};


	/* One way for every websitehq component to call an app's endpoint and to accept a link. */
	const METHOD = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)+$/;
	const localPath = (value) => (/^\/(?![\/\\])/.test(value || "") ? value : "");
	function parseError(body) {
		try {
			const list = JSON.parse(body._server_messages || "[]");
			if (list.length) return JSON.parse(list[0]).message.replace(/<[^>]+>/g, "");
		} catch (e) { /* fall through */ }
		return "";
	}
	/* method is a full dotted path. Resolves {ok, data} or {ok:false, message}; never throws. */
	async function call(method, args, { csrf, fallback = "", keepalive = false } = {}) {
		if (!METHOD.test(method || "")) return { ok: false, message: fallback };
		try {
			const response = await fetch("/api/method/" + method, {
				method: "POST", credentials: "same-origin",
				headers: { "Content-Type": "application/json", Accept: "application/json", "X-Frappe-CSRF-Token": csrf || (window.frappe && frappe.csrf_token) || "" },
				body: JSON.stringify(args || {}),
				keepalive, // lets the request finish after the page has closed
			});
			const body = await response.json().catch(() => ({}));
			return response.ok ? { ok: true, data: body.message } : { ok: false, message: parseError(body) || fallback };
		} catch (e) { return { ok: false, message: fallback }; }
	}

	/* Tabs: items = [{key, label}] */
	function segmented({ items, active, onChange, label }) {
		const el = h("div", { class: "rfhq-segmented", role: "tablist", "aria-label": label || "" });
		const buttons = {};
		items.forEach((item) => {
			const button = h("button", { type: "button", role: "tab", class: "rfhq-segmented__item", "data-key": item.key, text: item.label });
			button.addEventListener("click", () => select(item.key, true));
			buttons[item.key] = button;
			el.append(button);
		});
		function select(key, notify) {
			Object.entries(buttons).forEach(([k, b]) => {
				b.classList.toggle("is-active", k === key);
				b.setAttribute("aria-selected", k === key ? "true" : "false");
			});
			if (notify && onChange) onChange(key);
		}
		select(active, false);
		return { el, select: (key) => select(key, false) };
	}

	/* One real input (so paste and SMS autofill work) drawn as separate boxes. */
	function codeInput({ length, label, onComplete }) {
		const boxes = Array.from({ length }, () => h("span", { class: "rfhq-code__box", "aria-hidden": "true" }));
		const input = h("input", {
			class: "rfhq-code__input", type: "text", inputmode: "numeric", autocomplete: "one-time-code",
			maxlength: length, pattern: "[0-9]*", "aria-label": label || "",
		});
		const el = h("div", { class: "rfhq-code" }, h("div", { class: "rfhq-code__boxes" }, boxes), input);
		function paint() {
			const value = input.value;
			boxes.forEach((box, i) => {
				box.textContent = value[i] || "";
				box.classList.toggle("is-filled", i < value.length);
				box.classList.toggle("is-current", i === Math.min(value.length, length - 1) && document.activeElement === input);
			});
		}
		input.addEventListener("input", () => {
			input.value = input.value.replace(/\D/g, "").slice(0, length);
			paint();
			if (input.value.length === length && onComplete) onComplete(input.value);
		});
		["focus", "blur"].forEach((name) => input.addEventListener(name, paint));
		paint();
		return {
			el,
			focus: () => input.focus(),
			clear: () => { input.value = ""; paint(); },
			get value() { return input.value; },
		};
	}

	/* Dots plus a number pad. Keyboard digits work too. */
	function pinPad({ length, label, onComplete }) {
		let value = "";
		const dots = Array.from({ length }, () => h("span", { class: "rfhq-pin__dot" }));
		const el = h("div", { class: "rfhq-pin", role: "group", "aria-label": label || "" },
			h("div", { class: "rfhq-pin__dots", "aria-hidden": "true" }, dots));
		const grid = h("div", { class: "rfhq-pin__pad" });
		function paint() { dots.forEach((dot, i) => dot.classList.toggle("is-filled", i < value.length)); }
		function press(digit) {
			if (value.length >= length) return;
			value += digit; paint();
			if (value.length === length && onComplete) onComplete(value);
		}
		function back() { value = value.slice(0, -1); paint(); }
		["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].forEach((key) => {
			if (!key) { grid.append(h("span", { class: "rfhq-pin__gap" })); return; }
			const isBack = key === "⌫";
			grid.append(h("button", {
				type: "button", class: "rfhq-pin__key", text: key,
				"aria-label": isBack ? "Delete" : key,
				onclick: () => (isBack ? back() : press(key)),
			}));
		});
		el.append(grid);
		// Digits typed anywhere on the page count while this pad is on screen (not inside a text field).
		function onKey(event) {
			if (!el.isConnected) { document.removeEventListener("keydown", onKey); return; }
			if (/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) return;
			if (/^\d$/.test(event.key)) press(event.key);
			else if (event.key === "Backspace") back();
		}
		document.addEventListener("keydown", onKey);
		return {
			el,
			clear: () => { value = ""; paint(); },
			shake: () => { el.classList.remove("is-shaking"); void el.offsetWidth; el.classList.add("is-shaking"); },
			get value() { return value; },
		};
	}

	window.rfhqUI = Object.assign(window.rfhqUI || {}, { h, segmented, codeInput, pinPad, call, localPath });
})();
